/**
 * Score-distribution histogram for the presenter results view. Single-series
 * bar; the tallest bucket carries the J&J-red emphasis, the rest recede to
 * warm gray — drawing from the same palette source as the field/capacity
 * screens (`@/lib/chart-theme`, derived from `features/views/colors.ts`), so it
 * reads as part of this product's system.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ScoreBucket } from "@/lib/api";
import { AXIS_TICK, EMPHASIS, GRID, SUPPORTING, TOOLTIP_STYLE } from "@/lib/chart-theme";

export function VoteScoreChart({ distribution }: { distribution: ScoreBucket[] }) {
  const hasVotes = distribution.some((bucket) => bucket.count > 0);
  const maxIndex = distribution.reduce(
    (best, bucket, index) => (bucket.count > (distribution[best]?.count ?? 0) ? index : best),
    0,
  );

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={distribution} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="score" tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#f1efed" }} />
          <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={48}>
            {distribution.map((_, index) => (
              <Cell key={index} fill={hasVotes && index === maxIndex ? EMPHASIS : SUPPORTING[1]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
